/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import Swal from 'sweetalert2';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import i18n from 'components/Translation/Translate';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

// Authentication layout components
import BranchPosTop from 'layouts/branch/BranchPosTop';
import BranchPosRight from 'layouts/branch/BranchPosRight';
import BranchPosBottom from 'layouts/branch/BranchPosBottom';
import * as Defi from 'services/Define';
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';
import nopic from 'assets/images/nopic.jpg';
import './PointOfSale.css';

const initAttr = {
  id: '',
  code: '',
  name: '',
  shortcode: '',
  poscart_height: '500',
  poscampaign_height: '300',
  image: nopic,
  signature: nopic,
  image_postop: nopic,
  image_posright: nopic,
  image_posbottom: nopic,
  pos_bg: '#FFFFFF',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initCart = {
  id: '',
  customer_id: '',
  customer_name: '',
  nettotal: '',
};

function PosMiniExample() {
  const mod = 'branch';
  const { t } = useTranslation();
  const { id } = useParams();
  const app = useSelector((state) => state[Defi.APP_STATE]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attr, setAttr] = useState(initAttr);
  const [vdo, setVdo] = useState({ id: '', filepath: '' });
  const [promotion, setPromotion] = useState([]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const uploadPosTop = (filedata) => {
    setLoading(true);

    FetchData('OPTIONS', `/branch/upload`, app.token, {
      type: 'postop',
      id: id,
      filedata: filedata,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr({
            ...attr,
            image_postop: result.url,
          });
          showNotify(t(`alert.editcomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(t(`alert.saveerror`), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const uploadPosRight = (filedata) => {
    setLoading(true);

    FetchData('OPTIONS', `/branch/upload`, app.token, {
      type: 'posright',
      id: id,
      filedata: filedata,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr({
            ...attr,
            image_posright: result.url,
          });
          showNotify(t(`alert.editcomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(t(`alert.saveerror`), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const uploadPosBottom = (filedata) => {
    setLoading(true);

    FetchData('OPTIONS', `/branch/upload`, app.token, {
      type: 'posbottom',
      id: id,
      filedata: filedata,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr({
            ...attr,
            image_posbottom: result.url,
          });
          showNotify(t(`alert.editcomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(t(`alert.saveerror`), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const edit = (item) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, item)
          .then((result) => {
            if (result.status && result.status === 'ok') {
              showNotify(t(`alert.editcomplete`), 'success');
            } else if (!result.status) {
              showNotify(t('alert.server_timeout'), 'error');
            } else {
              showNotify(t(`alert.saveerror`), 'error');
            }
          })
          .catch((error) => {
            showNotify(t('alert.network_timeout'), 'error');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const init = () => {
    FetchData('GET', `/branch/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setVdo(result.vdo);
        setPromotion(result.slideshow);
        setLoading(false);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderOrder = () => {
    return (
      <TableContainer sx={{ height: attr.poscart_height * 1, boxShadow: 0, m: 0, p: 0 }}>
        <Table aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell align='center' width={10}>
                No.
              </TableCell>
              <TableCell>Barcode</TableCell>
              <TableCell>สินค้า</TableCell>
              <TableCell align='right'>จำนวน</TableCell>
              <TableCell align='left'>หน่วย</TableCell>
              <TableCell align='right'>ราคา/หน่วย</TableCell>
              <TableCell align='right'>ราคา</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(Array(16)).map((_, i) => (
              <TableRow className='row-order' key={i.toString()}>
                <TableCell align='center'>{i + 1}</TableCell>
                <TableCell>&nbsp;</TableCell>
                <TableCell>&nbsp;</TableCell>
                <TableCell>&nbsp;</TableCell>
                <TableCell>&nbsp;</TableCell>
                <TableCell>&nbsp;</TableCell>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSummary = () => {
    return (
      <TableContainer sx={{ boxShadow: 0 }}>
        <Table aria-label='summary table'>
          <TableBody>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>มูลค่าสินค้า :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>0.00</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>ภาษี :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>0.00</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>รวม :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>0.00</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>ส่วนลด :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>0.00</MDTypography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align='right'>
                <MDTypography variant='h5'>รวมทั้งสิ้น :</MDTypography>
              </TableCell>
              <TableCell align='right'>
                <MDTypography variant='h5'>0.00</MDTypography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCampaign = () => {
    return (
      <MDBox mt={1}>
        <TableContainer sx={{ height: attr.poscampaign_height * 1, boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center' width={10}>
                  No.
                </TableCell>
                <TableCell width={100}>Code</TableCell>
                <TableCell>โปรโมชั่น</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from(Array(10)).map((_, i) => (
                <TableRow className='row-order' key={i.toString()}>
                  <TableCell align='center'>{i + 1}</TableCell>
                  <TableCell>&nbsp;</TableCell>
                  <TableCell>&nbsp;</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderChange = () => {
    return (
      <MDBox className='box-change'>
        <div className='label'>เงินทอน</div>
        <div className='amount'>0.00</div>
        <div className='baht'>บาท</div>
      </MDBox>
    );
  };

  const renderSlideshow = () => {
    const settings = {
      autoplay: true,
      autoplaySpeed: 3000,
      className: 'center',
      infinite: true,
      centerPadding: '60px',
      slidesToShow: 1,
      speed: 500,
      arrows: false,
    };
    const cssImg = { width: '100%', borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px' };
    return (
      <MDBox>
        <Slider {...settings}>
          {promotion &&
            promotion.map((item) => (
              <div key={item.id}>
                <img src={item.image} style={cssImg} alt={item.name} height='160' />
              </div>
            ))}
        </Slider>
      </MDBox>
    );
  };

  const renderVideo = () => {
    return (
      <MDBox mt={1} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <video controls src={vdo.filepath} width='100%' loop>
          <track default kind='captions' />
        </video>
      </MDBox>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <div>
      <MDBox mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <MDInput
              label='สีพื้ันหลัง'
              onChange={(e) => setAttr({ ...attr, pos_bg: e.target.value })}
              value={attr.pos_bg}
              variant='outlined'
              type='color'
              fullWidth
              InputProps={{
                inputProps: {
                  autoComplete: 'off',
                },
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDButton
                      variant='text'
                      tabIndex={-1}
                      color='success'
                      onClick={() => edit({ pos_bg: attr.pos_bg })}
                    >
                      <Icon>save</Icon>&nbsp;{t(`element.save`)}
                    </MDButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <MDInput
              label='ความสูง รายการขาย'
              onChange={(e) => setAttr({ ...attr, poscart_height: e.target.value })}
              value={attr.poscart_height}
              variant='outlined'
              type='number'
              fullWidth
              InputProps={{
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDButton
                      variant='text'
                      tabIndex={-1}
                      color='success'
                      onClick={() => edit({ poscart_height: attr.poscart_height })}
                    >
                      <Icon>save</Icon>&nbsp;{t(`element.save`)}
                    </MDButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <MDInput
              label='ความสูง Campaign'
              onChange={(e) => setAttr({ ...attr, poscampaign_height: e.target.value })}
              value={attr.poscampaign_height}
              variant='outlined'
              type='number'
              fullWidth
              InputProps={{
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDButton
                      variant='text'
                      tabIndex={-1}
                      color='success'
                      onClick={() => edit({ poscampaign_height: attr.poscampaign_height })}
                    >
                      <Icon>save</Icon>&nbsp;{t(`element.save`)}
                    </MDButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
      <MDBox p={1} sx={{ backgroundColor: attr.pos_bg }}>
        <Grid container spacing={1}>
          <Grid item xs={8}>
            <BranchPosTop uploadData={uploadPosTop} parent={attr} />
          </Grid>
          <Grid item xs={4}>
            {renderChange()}
          </Grid>
          <Grid item xs={5}>
            {renderOrder()}
          </Grid>
          <Grid item xs={3}>
            {renderSummary()}
            {renderCampaign()}
          </Grid>
          <Grid item xs={4}>
            <BranchPosRight uploadData={uploadPosRight} parent={attr} />
            {renderVideo()}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: 1 }}>
          <Grid item xs={8}>
            {renderSlideshow()}
          </Grid>
          <Grid item xs={4}>
            <BranchPosBottom uploadData={uploadPosBottom} parent={attr} />
          </Grid>
        </Grid>
      </MDBox>
      {renderLoading()}
      {notify}
    </div>
  );
}

export default PosMiniExample;
